import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { CustomerInfo, Order, OrderItem, OrderLocation, SessionCreate, TimeSlot } from '../models';
import {
  ClearStateAction,
  DiscountAction,
  NoFailureAction,
  OrderCompleteAction,
  OrderDetailsAction,
  SessionCreateAction,
  SessionRetrieveAction,
  VerifyOrderAction,
  setPeriodId,
  setOrderLocation,
  setAccountID,
  setTimeSlot,
  setDeliveryLocation,
  setTable,
  setChildDeliveryLocation,
  setOrderCompletedOn,
  showGuestCheckoutAlert,
  setCustomerInfo,
  addOrderItemAction,
  setReOrderItems,
  AutoUpdateTimeSlotIfExpiredAction,
  completePaymentInProcessAction,
  setOrdertID,
  checkOrderStatusAction,
  checkTopUpStatusAction,
  setOrderNotes,
} from './order.actions';
import { IOrderFeature } from './order.feature.state';
import {
  getChildDeliveryLocation,
  getCustomerInfo,
  getDeliveryLocation,
  getDiscount,
  getFailures,
  getGuestCheckoutOrderID,
  getIsApplyingDiscount,
  getIsLoading,
  getOrderCompletedOn,
  getOrderDetails,
  getOrderID,
  getOrderItems,
  getOrderLocation,
  getOrderNotes,
  getOrderVerified,
  getPaymentSessionCreate,
  getPaymentSessionIntent,
  getPaymentSessionRetrieve,
  getReOrderItems,
  getTable,
  getTimeSlot,
} from './order.selectors';
import { filter, map } from 'rxjs/operators';
import { ChildDeliveryLocation, DeliveryLocationData, Table } from '@symphony-solo/domain/choose-location/src';
import { combineLatest } from 'rxjs';

@Injectable()
export class OrderFacade {
  public isLoading$ = this.store.select(getIsLoading);
  public isApplyingDiscount$ = this.store.select(getIsApplyingDiscount);
  public failures$ = this.store.select(getFailures);
  public orderDetails$ = this.store.select(getOrderDetails);

  public getOrderVerified$ = this.store.select(getOrderVerified);
  public getDiscount$ = this.store.select(getDiscount);
  public getdiscount$ = this.store.select(getDiscount).pipe(filter((discounts) => discounts !== null));
  public getPaymentSessionCreate$ = this.store.select(getPaymentSessionCreate);
  public getPaymentSessionRetrieve$ = this.store.select(getPaymentSessionRetrieve);

  public discountTotal$ = this.getDiscount$.pipe(
    map((discounts) => {
      if (discounts) {
        return discounts.reduce((accumulator, current) => {
          return +(accumulator + +current.value).toFixed(2);
        }, 0);
      }
      return 0;
    })
  );

  public timeSlot$ = this.store.select(getTimeSlot);
  public timeSlotInfo$ = this.timeSlot$.pipe(
    map((timeSlot) => {
      return timeSlot;
    })
  );

  public orderLocation$ = this.store.select(getOrderLocation);
  public deliveryLocation$ = this.store.select(getDeliveryLocation);
  public childDeliveryLocation$ = this.store.select(getChildDeliveryLocation);
  public table$ = this.store.select(getTable);

  public deliveryLocationText$ = combineLatest([this.deliveryLocation$, this.childDeliveryLocation$]).pipe(
    map(([deliveryLocation, childDeliveryLocation]) => {
      if (deliveryLocation && deliveryLocation.location !== '-1') {
        if (childDeliveryLocation !== null) {
          return childDeliveryLocation.location;
        }
        return deliveryLocation.location;
      }
    })
  );

  public orderCompletedOn$ = this.store.select(getOrderCompletedOn);

  public guestCheckoutOrderID$ = this.store.select(getGuestCheckoutOrderID);

  public isLocationAllowGuestCheckout$ = this.orderLocation$.pipe(
    map((orderLocation) => {
      return orderLocation.guestCheckout;
    })
  );

  public customerInfo$ = this.store.select(getCustomerInfo);

  public orderItems$ = this.store.select(getOrderItems);

  public reOrderItems$ = this.store.select(getReOrderItems);

  public orderNotes$ = this.store.select(getOrderNotes);

  // public subTotal$ = this.orderItems$.pipe(
  //   map((cartItems) => {
  //     return cartItems.reduce((accumulator, currentValue) => {
  //       return +(accumulator + +this.currentItemTotal(currentValue)).toFixed(2);
  //     }, 0);
  //   })
  // );

  // public total$ = combineLatest([this.subTotal$, this.discountTotal$]).pipe(
  //   concatMap(([cartSubtotal, discountTotal]) => {
  //     return of(+cartSubtotal - +discountTotal);
  //   })
  // );

  public orderID$ = this.store.select(getOrderID);

  public orderData$ = combineLatest([this.orderLocation$, this.timeSlot$]).pipe(
    map(([orderLocation, timeSlot]) => {
      return {
        timeSlot,
        orderLocation,
      };
    })
  );

  public cardPaymentInProcess$ = this.getPaymentSessionCreate$.pipe(
    map((session) => {
      return session && session !== null ? true : false;
    })
  );

  public paymentSessionIntent$ = this.store.select(getPaymentSessionIntent);

  constructor(private store: Store<IOrderFeature>) {}

  public verifyOrder(order: Order): void {
    this.store.dispatch(VerifyOrderAction({ order }));
  }

  // public applyDiscount(order: DiscountModal): void {
  //   this.store.dispatch(DiscountAction(order));
  // }

  public applyDiscount(createSessionAction = false): void {
    this.store.dispatch(DiscountAction({ createSessionAction }));
  }

  public autoUpdateTimeSlotIfExpired(): void {
    this.store.dispatch(AutoUpdateTimeSlotIfExpiredAction({ reCreateSession: true }));
  }

  public sessionCreate(sessionCreate: SessionCreate): void {
    this.store.dispatch(SessionCreateAction({ sessionCreate }));
  }

  public sessionRetrieve(completePaymentInProcess = false): void {
    this.store.dispatch(SessionRetrieveAction({ completePaymentInProcess }));
  }

  public checkOrderStatus(id?): void {
    this.store.dispatch(checkOrderStatusAction({ id }));
  }

  public checkTopUpStatus(id?): void {
    this.store.dispatch(checkTopUpStatusAction({ id }));
  }

  public orderComplete(): void {
    this.store.dispatch(OrderCompleteAction());
  }

  public noFailure(): void {
    this.store.dispatch(NoFailureAction());
  }

  public clearState(actionType?: string): void {
    this.store.dispatch(ClearStateAction({ actionType }));
  }

  public getOrderDetails(id: number): void {
    this.store.dispatch(OrderDetailsAction({ id }));
  }

  public setPeriodId(periodId: number): void {
    this.store.dispatch(setPeriodId({ periodId }));
  }

  public setOrderLocation(orderLocation: OrderLocation): void {
    this.store.dispatch(setOrderLocation({ orderLocation }));
  }

  public setAccountID(accountID: number): void {
    this.store.dispatch(setAccountID({ accountID }));
  }

  public setOrderID(orderID: number): void {
    this.store.dispatch(setOrdertID({ orderID }));
  }

  public setTimeSlot(timeSlot: TimeSlot): void {
    this.store.dispatch(setTimeSlot({ timeSlot }));
  }

  public setDeliveryLocation(deliveryLocation: DeliveryLocationData): void {
    this.store.dispatch(setDeliveryLocation({ deliveryLocation }));
  }

  public setChildDeliveryLocation(childDeliveryLocation: ChildDeliveryLocation): void {
    this.store.dispatch(setChildDeliveryLocation({ childDeliveryLocation }));
  }

  public setTable(table: Table): void {
    this.store.dispatch(setTable({ table }));
  }

  public setOrderCompletedOn(orderCompletedOn: string): void {
    this.store.dispatch(setOrderCompletedOn({ orderCompletedOn }));
  }

  public showGuestCheckoutAlert(): void {
    this.store.dispatch(showGuestCheckoutAlert());
  }

  public setCustomerInfo(customerInfo: CustomerInfo): void {
    this.store.dispatch(setCustomerInfo({ customerInfo }));
  }

  public addOrderItem(orderItem: OrderItem, cartItemIndex?): void {
    this.store.dispatch(addOrderItemAction({ orderItem, cartItemIndex }));
  }

  public setReOrderItems(reOrderItems: OrderItem[]): void {
    this.store.dispatch(setReOrderItems({ reOrderItems }));
  }

  public completePaymentInProcess(): void {
    this.store.dispatch(completePaymentInProcessAction());
  }

  public setOrderNotes(notes: string): void {
    this.store.dispatch(setOrderNotes({ notes }));
  }

  // private currentItemTotal(item: any): number {
  //   const price = item.oPrice * item.quantity;
  //   const itemTotal = { ...item.total, price };
  //   for (const i of item.childItems) {
  //     if (+i.total.price) {
  //       itemTotal.price += +i.total.price;
  //     }
  //   }
  //   return +itemTotal.price;
  // }
}
